import React, { useMemo, useState } from 'react'
import {
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { useModal } from '../../../../../store/ModalProvider'
import type { ServicesByClientModalData, ServicesByClientRow } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const servicesTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 12px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.04)',
	},
}

const defaultRows: ServicesByClientRow[] = [
	{
		id: '1',
		dataInicio: '02/05/2024',
		horaInicio: '08:00',
		servico: 'Transfer',
		localInicio: 'Lisboa',
		localTermino: 'Faro',
		horaTermino: '11:00',
		dataTermino: '02/05/2024',
		cliente: 'Best Transfers 4 U',
		observacoes: 'Sem observações',
		motorista: 'João Oliveira',
		viatura: '0062',
	},
	{
		id: '2',
		dataInicio: '02/05/2024',
		horaInicio: '09:00',
		servico: 'Excursão',
		localInicio: 'Setúbal',
		localTermino: 'Sintra',
		horaTermino: '12:00',
		dataTermino: '02/05/2024',
		cliente: 'Transol Corporate',
		observacoes: 'Cliente VIP',
		motorista: 'Ana Martins',
		viatura: '0046',
	},
	{
		id: '3',
		dataInicio: '02/05/2024',
		horaInicio: '13:00',
		servico: 'Transfer',
		localInicio: 'Porto',
		localTermino: 'Braga',
		horaTermino: '14:30',
		dataTermino: '02/05/2024',
		cliente: 'Best Transfers 4 U',
		observacoes: '2 bagagens',
		motorista: 'Rui Silva',
		viatura: '0021',
	},
	{
		id: '4',
		dataInicio: '02/05/2024',
		horaInicio: '15:00',
		servico: 'Turismo',
		localInicio: 'Almada',
		localTermino: 'Évora',
		horaTermino: '18:30',
		dataTermino: '02/05/2024',
		cliente: 'Frota Azul',
		observacoes: 'Guia incluído',
		motorista: 'Beatriz Costa',
		viatura: '0018',
	},
]

const CustomModalServicesByClient: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as ServicesByClientModalData | undefined
	const [handleExport, handlePrint] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const clientOptions =
		data?.clientes?.length && data.clientes.length > 0
			? data.clientes
			: ['Best Transfers 4 U', 'Transol Corporate', 'Frota Azul']
	const [client, setClient] = useState<string>(data?.cliente ?? clientOptions[0])
	const [density, setDensity] = useState<DensityMode>('standard')

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const columns = useMemo<GridColDef<ServicesByClientRow>[]>(
		() => [
			{ field: 'dataInicio', headerName: 'Data Início', minWidth: 110 },
			{ field: 'horaInicio', headerName: 'Hora Início', minWidth: 90 },
			{ field: 'servico', headerName: 'Serviço', minWidth: 120, flex: 1 },
			{ field: 'localInicio', headerName: 'Local Início', minWidth: 130 },
			{ field: 'localTermino', headerName: 'Local Término', minWidth: 130 },
			{ field: 'horaTermino', headerName: 'Hora Término', minWidth: 100 },
			{ field: 'dataTermino', headerName: 'Data Término', minWidth: 110 },
			{ field: 'cliente', headerName: 'Cliente', minWidth: 140 },
			{ field: 'observacoes', headerName: 'Observações', minWidth: 160, flex: 1 },
			{ field: 'motorista', headerName: 'Motorista', minWidth: 140 },
			{ field: 'viatura', headerName: 'Viatura', minWidth: 90 },
		],
		[],
	)

	const rows = data?.rows?.length ? data.rows : defaultRows

	if (!data) {
		return null
	}

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setClient(clientOptions[0])
	}

	const handleExportRows = () => {
		if (typeof handleExport === 'function') {
			handleExport(rows)
			return
		}
		console.log('Exportar serviços por cliente', rows)
	}

	const handlePrintRows = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir serviços por cliente', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Serviços por Cliente</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '16px 24px 0', display: 'grid', gap: '16px' }}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '12px',
								alignItems: 'flex-end',
								justifyContent: 'space-between',
							}}
						>
							<Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
								<DatePicker
									label='Data Início'
									value={startDate}
									onChange={value => setStartDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
								<DatePicker
									label='Data Fim'
									value={endDate}
									onChange={value => setEndDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
								<TextField
									label='Cliente'
									value={client}
									onChange={event => setClient(event.target.value)}
									select
									sx={[inputSx, { minWidth: '220px' }]}
									SelectProps={{
										IconComponent: KeyboardArrowDownIcon,
									}}
								>
									{clientOptions.map(option => (
										<MenuItem
											key={option}
											value={option}
										>
											{option}
										</MenuItem>
									))}
								</TextField>
							</Box>
							<CustomButton
								variant='text'
								size='small'
								onClick={handleClearFilters}
								sx={{
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '12px',
									textTransform: 'none',
								}}
							>
								Limpar Filtros
							</CustomButton>
						</Box>
					</LocalizationProvider>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '12px',
						}}
					>
						<Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{rows.length} Serviços</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, next) => next && setDensity(next)}
							sx={toggleGroupSx}
						>
							<ToggleButton value='compact'>
								<TableRowsIcon sx={{ fontSize: 16, color: '#092056' }} />
							</ToggleButton>
							<ToggleButton value='standard'>
								<ViewHeadlineIcon sx={{ fontSize: 16, color: '#092056' }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						animateRows={animateRows}
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						pagination={true}
						pageSize={10}
						sx={[tableSx, servicesTableSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '16px 24px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handleExportRows}
						sx={popupSecondaryButtonSx}
					>
						Exportar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handlePrintRows}
						sx={popupActionButtonSx}
					>
						Imprimir
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalServicesByClient
